import { useEffect } from "react";
import { createContainer } from "unstated-next";

import { Auth } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";
import { useMutation, useQuery, useQueryClient } from "react-query";

import createNotification from "shared/@utils/createNotification";

export function useOrders(iboNumber) {
  const queryClient = useQueryClient();
  const { makeAuthedRequest } = Auth.useContainer();
  const { logError } = useLogger({ prefix: "useOrders" });

  const { isLoading, isError, data, error, refetch } = useQuery(
    ["legacyibo", iboNumber],
    async () => {
      if (iboNumber == null) {
        return null;
      }

      return makeAuthedRequest(`v1/events/orders/ibo/${iboNumber}`).then(
        async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const body = await res.json();
            throw new Error(body.message);
          }
        }
      );
    }
  );

  useEffect(() => {
    if (isError) {
      logError(error);
    }
  }, [isError, error]);

  const revokeOrder = useMutation(
    async (orderToRevoke) => {
      return makeAuthedRequest(
        `v1/events/order/revoke`,

        {
          method: "put",
          body: JSON.stringify(orderToRevoke),
        }
      );
    },
    {
      onSuccess: () => {
        // Just refesh the whole event for now
        //queryClient.invalidateQueries(["event", eventId]);

        createNotification({
          type: "success",
          title: "Updated",
          message: "Order has been revoked.",
          autoDismiss: true,
        });
      },
      onError: (err) => {
        createNotification({
          type: "danger",
          title: "Error",
          message: err.message || "Something went wrong!",
          autoDismiss: true,
        });
      },
    }
  );

  const checkIn = useMutation(
    async (ticketDto) => {
      return makeAuthedRequest(
        `v1/events/ticket/check-in`,

        {
          method: "put",
          body: JSON.stringify(ticketDto),
        }
      );
    },
    {
      onSuccess: () => {
        // Just refesh everything for now
        queryClient.invalidateQueries(["legacyibo", iboNumber]);

        createNotification({
          type: "success",
          title: "Updated",
          message: "Ticket has been checked in.",
          autoDismiss: true,
        });
      },
      onError: (err) => {
        createNotification({
          type: "danger",
          title: "Error",
          message: err.message || "Something went wrong!",
          autoDismiss: true,
        });
      },
    }
  );

  const uncheckTicket = useMutation(
    async (ticketDto) => {
      return makeAuthedRequest(
        `v1/events/ticket/check-in`,

        {
          method: "put",
          body: JSON.stringify(ticketDto),
        }
      );
    },
    {
      onSuccess: () => {
        // Just refesh everything for now
        queryClient.invalidateQueries(["legacyibo", iboNumber]);

        createNotification({
          type: "success",
          title: "Updated",
          message: "Check In has been removed.",
          autoDismiss: true,
        });
      },
      onError: (err) => {
        createNotification({
          type: "danger",
          title: "Error",
          message: err.message || "Something went wrong!",
          autoDismiss: true,
        });
      },
    }
  );

  const updateNote = useMutation(({ orderId, noteData }) =>
    makeAuthedRequest(`v1/events/order/notes/${orderId}`, {
      method: "PUT",
      body: JSON.stringify(noteData),
    }).then(async (res) => {
      if (res.ok) {
        return res.status;
      } else {
        const body = await res.json();
        throw new Error(body.error);
      }
    })
  );

  return {
    isLoading,
    isError,
    orders: data,
    revokeOrder,
    checkIn,
    uncheckTicket,
    error,
    refreshIBOInfo: refetch,
    updateNote,
  };
}

export const Orders = createContainer(useOrders);
